import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, ChevronRight, ChevronLeft, Edit2, Award, Package, DollarSign, 
  Clock, Calendar, Target, Crown, LogOut, Bell, Shield, HelpCircle, Star, 
  Briefcase, TrendingUp, User, Mail, Phone, MapPin, CreditCard, Eye, 
  EyeOff, Lock, MessageSquare, FileText, ExternalLink, Save, X, Globe, Check, ScrollText, AlertCircle, Loader2, ShieldAlert, AlertTriangle, History, CheckCircle, XCircle, ClockIcon
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { authAPI, sessionsAPI, jobsAPI } from '../services/api';
import { contractContent } from '../data/contractContent';
import { safetyRules, safetyWarning } from '../data/safetyRules';
import SupportChat from '../components/SupportChat';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage, supportedLanguages, getCurrentLanguage } = useLanguage();
  const { user, refreshUser, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('stats');
  const [settingsView, setSettingsView] = useState('main'); // main, notifications, goals, privacy, help, account, language, contracts, safety, requestHistory
  
  // Request history state
  const [requestHistory, setRequestHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [signingContract, setSigningContract] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const contractScrollRef = useRef(null);
  const [canSign, setCanSign] = useState(false);
  const [acknowledgedRead, setAcknowledgedRead] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Safety agreement states
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [safetyChecks, setSafetyChecks] = useState({});
  const [signingSafety, setSigningSafety] = useState(false);
  
  // Fetch user stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const userStats = await sessionsAPI.getMyStats();
        setStats(userStats);
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, []);
  
  // Default stats when no data available
  const defaultStats = {
    totalDeliveries: 0,
    totalEarnings: 0,
    totalJobs: 0,
    totalHours: 0,
    weeklyDeliveries: 0,
    weeklyEarnings: 0,
    daysWorked: 0,
  };
  
  const displayStats = stats || defaultStats;
  
  // Settings states
  const [notifications, setNotifications] = useState({
    pushEnabled: true,
    newJobs: true,
    jobReminders: true,
    paymentAlerts: true,
    weeklyReport: false,
    promotions: false,
  });
  
  const [goals, setGoals] = useState({
    dailyDeliveries: 500,
    weeklyEarnings: 500,
    weeklyJobs: 5,
    reminderTime: '08:00',
    reminderEnabled: true,
  });
  
  const [privacy, setPrivacy] = useState({
    showProfile: true,
    showEarnings: false,
    showStats: true,
    locationTracking: true,
    dataSharing: false,
  });
  
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    paypalEmail: user?.paypal_email || '',
  });
  
  // Update profile when user changes
  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        paypalEmail: user.paypal_email || '',
      });
    }
  }, [user]);
  
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState({ ...profile });

  const achievements = [
    { id: 1, name: 'First Delivery', icon: Package, color: 'from-green-400 to-emerald-500', earned: true },
    { id: 2, name: '1000 Flyers', icon: TrendingUp, color: 'from-blue-400 to-cyan-500', earned: true },
    { id: 3, name: '7 Day Streak', icon: Star, color: 'from-orange-400 to-red-500', earned: true },
    { id: 4, name: '30 Day Streak', icon: Award, color: 'from-yellow-400 to-amber-500', earned: false },
    { id: 5, name: '$1000 Earned', icon: DollarSign, color: 'from-purple-400 to-pink-500', earned: true },
    { id: 6, name: 'Early Bird', icon: Clock, color: 'from-pink-400 to-rose-500', earned: true },
  ];

  const handleSaveProfile = () => {
    setProfile(editingProfile);
    setShowEditProfile(false);
    toast.success('Profile updated successfully');
  };

  const handleSaveGoals = () => {
    toast.success('Goals saved successfully');
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Settings sub-views
  const renderSettingsContent = () => {
    switch (settingsView) {
      case 'notifications':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSettingsView('main')} className="p-2 -ml-2">
                <ChevronLeft size={24} className="text-gray-600" />
              </button>
              <h2 className="text-lg font-bold text-gray-900">Notifications</h2>
            </div>
            
            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Push Notifications</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Enable Notifications</p>
                    <p className="text-xs text-gray-500">Receive push notifications</p>
                  </div>
                  <Switch 
                    checked={notifications.pushEnabled}
                    onCheckedChange={(checked) => setNotifications({...notifications, pushEnabled: checked})}
                  />
                </div>
              </div>
            </Card>

            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Alert Types</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">New Jobs Available</p>
                    <p className="text-xs text-gray-500">Get notified when new jobs match your area</p>
                  </div>
                  <Switch 
                    checked={notifications.newJobs}
                    onCheckedChange={(checked) => setNotifications({...notifications, newJobs: checked})}
                    disabled={!notifications.pushEnabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Job Reminders</p>
                    <p className="text-xs text-gray-500">Reminders for active job deadlines</p>
                  </div>
                  <Switch 
                    checked={notifications.jobReminders}
                    onCheckedChange={(checked) => setNotifications({...notifications, jobReminders: checked})}
                    disabled={!notifications.pushEnabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Payment Alerts</p>
                    <p className="text-xs text-gray-500">Get notified when you receive payment</p>
                  </div>
                  <Switch 
                    checked={notifications.paymentAlerts}
                    onCheckedChange={(checked) => setNotifications({...notifications, paymentAlerts: checked})}
                    disabled={!notifications.pushEnabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Weekly Summary</p>
                    <p className="text-xs text-gray-500">Weekly earnings and stats report</p>
                  </div>
                  <Switch 
                    checked={notifications.weeklyReport}
                    onCheckedChange={(checked) => setNotifications({...notifications, weeklyReport: checked})}
                    disabled={!notifications.pushEnabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Promotions & Tips</p>
                    <p className="text-xs text-gray-500">Bonus opportunities and delivery tips</p>
                  </div>
                  <Switch 
                    checked={notifications.promotions}
                    onCheckedChange={(checked) => setNotifications({...notifications, promotions: checked})}
                    disabled={!notifications.pushEnabled}
                  />
                </div>
              </div>
            </Card>
          </div>
        );

      case 'goals':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSettingsView('main')} className="p-2 -ml-2">
                <ChevronLeft size={24} className="text-gray-600" />
              </button>
              <h2 className="text-lg font-bold text-gray-900">Goals & Reminders</h2>
            </div>
            
            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Daily Goals</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-600">Daily Delivery Goal</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Input 
                      type="number"
                      value={goals.dailyDeliveries}
                      onChange={(e) => setGoals({...goals, dailyDeliveries: parseInt(e.target.value) || 0})}
                      className="w-24"
                    />
                    <span className="text-sm text-gray-500">deliveries per day</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Weekly Goals</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-600">Weekly Earnings Goal</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-gray-500">$</span>
                    <Input 
                      type="number"
                      value={goals.weeklyEarnings}
                      onChange={(e) => setGoals({...goals, weeklyEarnings: parseInt(e.target.value) || 0})}
                      className="w-24"
                    />
                    <span className="text-sm text-gray-500">per week</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Weekly Jobs Goal</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Input 
                      type="number"
                      value={goals.weeklyJobs}
                      onChange={(e) => setGoals({...goals, weeklyJobs: parseInt(e.target.value) || 0})}
                      className="w-24"
                    />
                    <span className="text-sm text-gray-500">jobs per week</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Daily Reminder</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Enable Daily Reminder</p>
                    <p className="text-xs text-gray-500">Get reminded to start delivering</p>
                  </div>
                  <Switch 
                    checked={goals.reminderEnabled}
                    onCheckedChange={(checked) => setGoals({...goals, reminderEnabled: checked})}
                  />
                </div>
                {goals.reminderEnabled && (
                  <div>
                    <Label className="text-sm text-gray-600">Reminder Time</Label>
                    <Input 
                      type="time"
                      value={goals.reminderTime}
                      onChange={(e) => setGoals({...goals, reminderTime: e.target.value})}
                      className="w-32 mt-1"
                    />
                  </div>
                )}
              </div>
            </Card>

            <Button onClick={handleSaveGoals} className="w-full bg-orange-500 hover:bg-orange-600">
              <Save size={18} className="mr-2" />
              Save Goals
            </Button>
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSettingsView('main')} className="p-2 -ml-2">
                <ChevronLeft size={24} className="text-gray-600" />
              </button>
              <h2 className="text-lg font-bold text-gray-900">Privacy & Safety</h2>
            </div>
            
            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Profile Visibility</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Show Profile to Others</p>
                    <p className="text-xs text-gray-500">Allow other users to see your profile</p>
                  </div>
                  <Switch 
                    checked={privacy.showProfile}
                    onCheckedChange={(checked) => setPrivacy({...privacy, showProfile: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Show Earnings</p>
                    <p className="text-xs text-gray-500">Display your earnings on leaderboards</p>
                  </div>
                  <Switch 
                    checked={privacy.showEarnings}
                    onCheckedChange={(checked) => setPrivacy({...privacy, showEarnings: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Show Stats</p>
                    <p className="text-xs text-gray-500">Display delivery stats publicly</p>
                  </div>
                  <Switch 
                    checked={privacy.showStats}
                    onCheckedChange={(checked) => setPrivacy({...privacy, showStats: checked})}
                  />
                </div>
              </div>
            </Card>

            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Data & Tracking</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Location Tracking</p>
                    <p className="text-xs text-gray-500">Required for delivery verification</p>
                  </div>
                  <Switch 
                    checked={privacy.locationTracking}
                    onCheckedChange={(checked) => setPrivacy({...privacy, locationTracking: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Analytics Data Sharing</p>
                    <p className="text-xs text-gray-500">Help improve app with usage data</p>
                  </div>
                  <Switch 
                    checked={privacy.dataSharing}
                    onCheckedChange={(checked) => setPrivacy({...privacy, dataSharing: checked})}
                  />
                </div>
              </div>
            </Card>

            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Security</h3>
              <div className="space-y-3">
                <button className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Lock size={20} className="text-gray-600" />
                    <span className="font-medium text-gray-900">Change Password</span>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
                <button className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Shield size={20} className="text-gray-600" />
                    <span className="font-medium text-gray-900">Two-Factor Authentication</span>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
              </div>
            </Card>
          </div>
        );

      case 'help':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSettingsView('main')} className="p-2 -ml-2">
                <ChevronLeft size={24} className="text-gray-600" />
              </button>
              <h2 className="text-lg font-bold text-gray-900">Help & Support</h2>
            </div>
            
            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Help</h3>
              <div className="space-y-3">
                <button className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-blue-500" />
                    <span className="font-medium text-gray-900">FAQs</span>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
                <button className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MessageSquare size={20} className="text-green-500" />
                    <span className="font-medium text-gray-900">Contact Support</span>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
                <button className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <ExternalLink size={20} className="text-purple-500" />
                    <span className="font-medium text-gray-900">Video Tutorials</span>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
              </div>
            </Card>

            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Common Questions</h3>
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900 text-sm">How do I get paid?</p>
                  <p className="text-xs text-gray-500 mt-1">Payments are processed via PayPal within 24-48 hours after job approval.</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900 text-sm">How does delivery verification work?</p>
                  <p className="text-xs text-gray-500 mt-1">GPS detects when you stop at a front door for 6 seconds to automatically credit the delivery.</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900 text-sm">Can I have multiple active jobs?</p>
                  <p className="text-xs text-gray-500 mt-1">No, you can only have one active job at a time to ensure quality deliveries.</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Legal</h3>
              <div className="space-y-3">
                <button className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-900">Terms of Service</span>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
                <button className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-900">Privacy Policy</span>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
                <button className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-900">Independent Contractor Agreement</span>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
              </div>
            </Card>

            <p className="text-center text-xs text-gray-400 mt-4">
              App Version 1.0.0
            </p>
          </div>
        );

      case 'account':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSettingsView('main')} className="p-2 -ml-2">
                <ChevronLeft size={24} className="text-gray-600" />
              </button>
              <h2 className="text-lg font-bold text-gray-900">Account Settings</h2>
            </div>
            
            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Personal Information</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <User size={18} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">{profile.name}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{profile.email}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{profile.phone}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MapPin size={18} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="font-medium text-gray-900">{profile.address}</p>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={() => {
                    setEditingProfile({...profile});
                    setShowEditProfile(true);
                  }}
                  variant="outline" 
                  className="w-full mt-2"
                >
                  <Edit2 size={16} className="mr-2" />
                  Edit Profile
                </Button>
              </div>
            </Card>

            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Payment Information</h3>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CreditCard size={18} className="text-blue-500" />
                  <div>
                    <p className="text-xs text-gray-500">PayPal Email</p>
                    <p className="font-medium text-gray-900">{profile.paypalEmail}</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Payments are sent to this PayPal account after job approval.
              </p>
            </Card>

            <Card className="bg-red-50 border border-red-200 p-4">
              <h3 className="font-semibold text-red-700 mb-2">Danger Zone</h3>
              <p className="text-xs text-red-600 mb-3">
                These actions are permanent and cannot be undone.
              </p>
              <button className="w-full p-3 bg-white border border-red-300 rounded-lg text-red-600 font-medium text-sm">
                Delete Account
              </button>
            </Card>
          </div>
        );

      case 'language':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSettingsView('main')} className="p-2 -ml-2">
                <ChevronLeft size={24} className="text-gray-600" />
              </button>
              <h2 className="text-lg font-bold text-gray-900">{t('language_title')}</h2>
            </div>
            
            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">{t('language_select')}</h3>
              <div className="space-y-2">
                {supportedLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      toast.success(lang.code === 'es' ? 'Idioma cambiado a Español' : 'Language changed to English');
                    }}
                    className={`flex items-center justify-between w-full p-4 rounded-xl transition-all ${
                      language === lang.code 
                        ? 'bg-orange-50 border-2 border-orange-500' 
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{lang.flag}</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{lang.nativeName}</p>
                        <p className="text-xs text-gray-500">{lang.name}</p>
                      </div>
                    </div>
                    {language === lang.code && (
                      <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                        <Check size={16} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </Card>

            <Card className="bg-blue-50 border border-blue-200 p-4">
              <div className="flex items-start gap-3">
                <Globe size={20} className="text-blue-500 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900 text-sm">
                    {language === 'es' ? 'Preferencia de Idioma' : 'Language Preference'}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {language === 'es' 
                      ? 'Tu preferencia de idioma se guardará y aplicará en toda la aplicación.'
                      : 'Your language preference will be saved and applied throughout the app.'}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'requestHistory':
        return (
          <div className="space-y-4" data-testid="request-history-view">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSettingsView('main')} className="p-2 -ml-2">
                <ChevronLeft size={24} className="text-gray-600" />
              </button>
              <h2 className="text-lg font-bold text-gray-900">Request History</h2>
            </div>
            
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : requestHistory.length === 0 ? (
              <Card className="bg-white border-0 shadow-sm p-8 text-center">
                <History size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">No Request History</h3>
                <p className="text-sm text-gray-500">
                  Your job request history will appear here once you start requesting jobs.
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {requestHistory.map((request) => (
                  <Card 
                    key={request.id} 
                    className={`bg-white border-0 shadow-sm p-4 ${
                      request.status === 'pending' ? 'border-l-4 border-l-amber-500' :
                      request.status === 'approved' ? 'border-l-4 border-l-green-500' :
                      request.status === 'denied' ? 'border-l-4 border-l-red-500' :
                      'border-l-4 border-l-gray-400'
                    }`}
                    data-testid={`request-history-item-${request.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{request.job_title}</h4>
                        <p className="text-sm text-gray-500">{request.job_client}</p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        request.status === 'approved' ? 'bg-green-100 text-green-700' :
                        request.status === 'denied' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {request.status === 'pending' && <Clock size={12} className="inline mr-1" />}
                        {request.status === 'approved' && <CheckCircle size={12} className="inline mr-1" />}
                        {request.status === 'denied' && <XCircle size={12} className="inline mr-1" />}
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div className="flex items-center gap-1 text-gray-500">
                        <MapPin size={14} />
                        <span className="truncate">{request.job_location || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-500">
                        <Package size={14} />
                        <span>{request.job_deliveries} deliveries</span>
                      </div>
                      <div className="flex items-center gap-1 text-green-600">
                        <DollarSign size={14} />
                        <span>${request.job_pay?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-500">
                        <Calendar size={14} />
                        <span>{new Date(request.requested_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    {request.status !== 'pending' && (
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-xs text-gray-500">
                          {request.status === 'approved' ? 'Approved' : 
                           request.status === 'denied' ? 'Denied' : 
                           'Resolved'} on {new Date(request.resolved_at).toLocaleDateString()}
                          {request.resolved_by_name && ` by ${request.resolved_by_name}`}
                        </p>
                        {request.notes && (
                          <p className="text-xs text-gray-600 mt-1 italic">
                            Note: {request.notes}
                          </p>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      default: // main settings view
        return (
          <div className="space-y-3">
            <Card 
              className="bg-white border-0 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setSettingsView('account')}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User size={20} className="text-blue-600" />
                </div>
                <span className="flex-1 font-medium text-gray-900">{t('settings_account')}</span>
                <ChevronRight size={20} className="text-gray-300" />
              </div>
            </Card>

            <Card 
              className="bg-white border-0 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setSettingsView('notifications')}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Bell size={20} className="text-orange-600" />
                </div>
                <span className="flex-1 font-medium text-gray-900">{t('settings_notifications')}</span>
                <ChevronRight size={20} className="text-gray-300" />
              </div>
            </Card>

            <Card 
              className="bg-white border-0 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setSettingsView('goals')}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Target size={20} className="text-green-600" />
                </div>
                <span className="flex-1 font-medium text-gray-900">{t('settings_goals')}</span>
                <ChevronRight size={20} className="text-gray-300" />
              </div>
            </Card>

            <Card 
              className="bg-white border-0 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setSettingsView('privacy')}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Shield size={20} className="text-purple-600" />
                </div>
                <span className="flex-1 font-medium text-gray-900">{t('settings_privacy')}</span>
                <ChevronRight size={20} className="text-gray-300" />
              </div>
            </Card>

            <Card 
              className="bg-white border-0 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setSettingsView('help')}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                  <HelpCircle size={20} className="text-cyan-600" />
                </div>
                <span className="flex-1 font-medium text-gray-900">{t('settings_help')}</span>
                <ChevronRight size={20} className="text-gray-300" />
              </div>
            </Card>

            <Card 
              className="bg-white border-0 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={async () => {
                setSettingsView('requestHistory');
                setLoadingHistory(true);
                try {
                  const history = await jobsAPI.getMyRequestHistory();
                  setRequestHistory(history.data || history);
                } catch (err) {
                  console.error('Error fetching request history:', err);
                  toast.error('Failed to load request history');
                } finally {
                  setLoadingHistory(false);
                }
              }}
              data-testid="request-history-menu-item"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <History size={20} className="text-indigo-600" />
                </div>
                <div className="flex-1">
                  <span className="font-medium text-gray-900">Request History</span>
                  <p className="text-xs text-gray-500">View your job request status</p>
                </div>
                <ChevronRight size={20} className="text-gray-300" />
              </div>
            </Card>

            <Card 
              className="bg-white border-0 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setSettingsView('contracts')}
              data-testid="contracts-menu-item"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <ScrollText size={20} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <span className="font-medium text-gray-900">{t('settings_contracts')}</span>
                  <p className="text-xs text-gray-500">
                    {user?.contract_signed ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <Check size={12} /> {t('contract_signed')}
                      </span>
                    ) : (
                      <span className="text-orange-600">{t('contract_not_signed')}</span>
                    )}
                  </p>
                </div>
                <ChevronRight size={20} className="text-gray-300" />
              </div>
            </Card>

            <Card 
              className="bg-white border-0 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setSettingsView('safety')}
              data-testid="safety-menu-item"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <ShieldAlert size={20} className="text-red-600" />
                </div>
                <div className="flex-1">
                  <span className="font-medium text-gray-900">Safety & Delivery Rules</span>
                  <p className="text-xs text-gray-500">
                    {user?.safety_agreed ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <Check size={12} /> Agreed
                      </span>
                    ) : (
                      <span className="text-red-600">Required before accepting jobs</span>
                    )}
                  </p>
                </div>
                <ChevronRight size={20} className="text-gray-300" />
              </div>
            </Card>

            <Card 
              className="bg-white border-0 shadow-sm p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setSettingsView('language')}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Globe size={20} className="text-indigo-600" />
                </div>
                <div className="flex-1">
                  <span className="font-medium text-gray-900">{t('settings_language')}</span>
                  <p className="text-xs text-gray-500">{getCurrentLanguage().nativeName} {getCurrentLanguage().flag}</p>
                </div>
                <ChevronRight size={20} className="text-gray-300" />
              </div>
            </Card>

            {/* Logout */}
            <Card className="bg-white border-0 shadow-sm p-4 mt-6">
              <button onClick={handleLogout} className="flex items-center gap-4 w-full text-red-500">
                <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                  <LogOut size={20} />
                </div>
                <span className="font-medium">{t('settings_logout')}</span>
              </button>
            </Card>
          </div>
        );

      case 'contracts':
        return (
          <div className="space-y-4" data-testid="contracts-settings">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSettingsView('main')} className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <ChevronLeft size={18} />
              </button>
              <h2 className="text-lg font-bold text-gray-900">{t('settings_contracts')}</h2>
            </div>
            
            {/* Contract Status Card */}
            <Card className="bg-white border-0 shadow-sm p-4">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  user?.contract_signed ? 'bg-green-100' : 'bg-orange-100'
                }`}>
                  {user?.contract_signed ? (
                    <Check size={24} className="text-green-600" />
                  ) : (
                    <AlertCircle size={24} className="text-orange-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{t('contract_title')}</h3>
                  <p className={`text-sm ${user?.contract_signed ? 'text-green-600' : 'text-orange-600'}`}>
                    {t('contract_status')}: {user?.contract_signed ? t('contract_signed') : t('contract_not_signed')}
                  </p>
                  {user?.contract_signed && user?.contract_signed_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t('contract_signed_on')}: {new Date(user.contract_signed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              
              <Button
                onClick={openContractModal}
                className={`w-full ${
                  user?.contract_signed 
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                }`}
                data-testid="review-contract-btn"
              >
                <ScrollText size={18} className="mr-2" />
                {user?.contract_signed ? t('contract_review') : t('contract_sign_button')}
              </Button>
            </Card>

            {!user?.contract_signed && (
              <Card className="bg-orange-50 border border-orange-200 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-orange-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-orange-800">
                    {t('contract_required_message')}
                  </p>
                </div>
              </Card>
            )}
          </div>
        );

      case 'safety':
        const allRulesChecked = safetyRules.every(rule => safetyChecks[rule.id]);
        return (
          <div className="space-y-4" data-testid="safety-settings">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSettingsView('main')} className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <ChevronLeft size={18} />
              </button>
              <h2 className="text-lg font-bold text-gray-900">Safety & Delivery Rules</h2>
            </div>
            
            {/* Safety Status Card */}
            <Card className="bg-white border-0 shadow-sm p-4">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  user?.safety_agreed ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {user?.safety_agreed ? (
                    <Check size={24} className="text-green-600" />
                  ) : (
                    <ShieldAlert size={24} className="text-red-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Delivery Guidelines</h3>
                  <p className={`text-sm ${user?.safety_agreed ? 'text-green-600' : 'text-red-600'}`}>
                    Status: {user?.safety_agreed ? 'Agreed' : 'Not yet agreed'}
                  </p>
                  {user?.safety_agreed && user?.safety_agreed_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      Agreed on: {new Date(user.safety_agreed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              
              <Button
                onClick={() => {
                  setSafetyChecks({});
                  setShowSafetyModal(true);
                }}
                className={`w-full ${
                  user?.safety_agreed 
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
                data-testid="review-safety-btn"
              >
                <ShieldAlert size={18} className="mr-2" />
                {user?.safety_agreed ? 'Review Safety Rules' : 'Read & Agree to Safety Rules'}
              </Button>
            </Card>

            {!user?.safety_agreed && (
              <Card className="bg-red-50 border border-red-200 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800 font-medium">
                      You must read and agree to all safety and delivery rules before you can accept jobs.
                    </p>
                    <p className="text-xs text-red-700 mt-1">
                      Breaking any of these rules will result in automatic termination of your contract.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        );
    }
  };

  // Handle contract signing
  const handleSignContract = async () => {
    try {
      setSigningContract(true);
      await authAPI.signContract();
      toast.success(t('contract_sign_success'));
      setShowContractModal(false);
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      console.error('Error signing contract:', err);
      toast.error(err.response?.data?.detail || 'Failed to sign contract');
    } finally {
      setSigningContract(false);
    }
  };

  // Handle safety agreement signing
  const handleSignSafety = async () => {
    const allChecked = safetyRules.every(rule => safetyChecks[rule.id]);
    if (!allChecked) {
      toast.error('Please check all safety rules before agreeing');
      return;
    }
    
    try {
      setSigningSafety(true);
      await authAPI.signSafety();
      toast.success('Safety agreement signed successfully!');
      setShowSafetyModal(false);
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      console.error('Error signing safety agreement:', err);
      toast.error(err.response?.data?.detail || 'Failed to sign safety agreement');
    } finally {
      setSigningSafety(false);
    }
  };

  // Handle contract scroll
  const handleContractScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Enable sign button when scrolled to bottom (within 100px for better mobile support)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      setCanSign(true);
    }
  };

  // Check scroll position on touch end (for iOS)
  const handleContractTouchEnd = () => {
    if (contractScrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contractScrollRef.current;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        setCanSign(true);
      }
    }
  };

  // Reset canSign when modal opens
  const openContractModal = () => {
    setCanSign(false);
    setAcknowledgedRead(false);
    setShowContractModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white px-5 pt-12 pb-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-xl font-bold">Profile</h1>
            <button 
              onClick={() => {
                setActiveTab('settings');
                setSettingsView('main');
              }}
              className="w-10 h-10 bg-white/15 rounded-full flex items-center justify-center"
            >
              <Settings size={20} />
            </button>
          </div>

          {/* Profile Card */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar className="w-20 h-20 border-4 border-white/30">
                <AvatarFallback className="bg-orange-400 text-white text-2xl">{user?.name?.charAt(0) || 'U'}</AvatarFallback>
              </Avatar>
              <button 
                onClick={() => {
                  setActiveTab('settings');
                  setSettingsView('account');
                }}
                className="absolute bottom-0 right-0 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-lg"
              >
                <Edit2 size={14} className="text-orange-600" />
              </button>
            </div>
            <div>
              <h2 className="text-xl font-bold">{user?.name || 'User'}</h2>
              <p className="text-orange-100 text-sm">{user?.email || ''}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                  <Star size={12} /> {user?.status === 'approved' ? t('profile_active') || 'Active' : user?.status || 'Pending'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="px-5 -mt-10 relative z-20">
        <div className="grid grid-cols-4 gap-2">
          <Card className="bg-white p-3 shadow-lg border-0 text-center">
            <p className="text-lg font-bold text-gray-900">{displayStats.totalJobs}</p>
            <p className="text-[10px] text-gray-500">{t('profile_jobs')}</p>
          </Card>
          <Card className="bg-white p-3 shadow-lg border-0 text-center">
            <p className="text-lg font-bold text-gray-900">{displayStats.totalDeliveries >= 1000 ? `${(displayStats.totalDeliveries / 1000).toFixed(1)}k` : displayStats.totalDeliveries}</p>
            <p className="text-[10px] text-gray-500">{t('profile_deliveries')}</p>
          </Card>
          <Card className="bg-white p-3 shadow-lg border-0 text-center">
            <p className="text-lg font-bold text-gray-900">{Math.round(displayStats.totalHours || 0)}</p>
            <p className="text-[10px] text-gray-500">{t('profile_hours')}</p>
          </Card>
          <Card className="bg-white p-3 shadow-lg border-0 text-center">
            <p className="text-lg font-bold text-gray-900">${displayStats.totalEarnings >= 1000 ? `${(displayStats.totalEarnings / 1000).toFixed(1)}k` : displayStats.totalEarnings.toFixed(0)}</p>
            <p className="text-[10px] text-gray-500">{t('profile_earned')}</p>
          </Card>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 mt-6">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {['stats', 'achievements', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                if (tab === 'settings') setSettingsView('main');
              }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all capitalize ${
                activeTab === tab
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              {t(`profile_${tab}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-5 mt-5">
        {activeTab === 'stats' && (
          <div className="space-y-4">
            {/* Weekly Summary */}
            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-bold text-gray-900 mb-4">{t('profile_this_week')}</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600 flex items-center gap-2">
                      <Package size={16} className="text-orange-500" /> {t('profile_deliveries')}
                    </span>
                    <span className="text-sm font-medium">{displayStats.weeklyDeliveries} / {goals.dailyDeliveries}</span>
                  </div>
                  <Progress value={(displayStats.weeklyDeliveries / goals.dailyDeliveries) * 100} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600 flex items-center gap-2">
                      <DollarSign size={16} className="text-green-500" /> {t('profile_earned')}
                    </span>
                    <span className="text-sm font-medium">${displayStats.weeklyEarnings.toFixed(2)} / ${goals.weeklyEarnings}</span>
                  </div>
                  <Progress value={(displayStats.weeklyEarnings / goals.weeklyEarnings) * 100} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600 flex items-center gap-2">
                      <Briefcase size={16} className="text-blue-500" /> {t('profile_jobs')}
                    </span>
                    <span className="text-sm font-medium">{displayStats.totalJobs} / {goals.weeklyJobs}</span>
                  </div>
                  <Progress value={(displayStats.totalJobs / goals.weeklyJobs) * 100} className="h-2" />
                </div>
              </div>
            </Card>

            {/* All-Time Stats */}
            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-bold text-gray-900 mb-4">{t('profile_all_time')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-orange-50 rounded-xl p-3">
                  <p className="text-sm text-gray-600">{t('profile_total_deliveries')}</p>
                  <p className="text-xl font-bold text-gray-900">{displayStats.totalDeliveries.toLocaleString()}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3">
                  <p className="text-sm text-gray-600">{t('profile_earned')}</p>
                  <p className="text-xl font-bold text-gray-900">${displayStats.totalEarnings.toFixed(2)}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3">
                  <p className="text-sm text-gray-600">{t('profile_hours')}</p>
                  <p className="text-xl font-bold text-gray-900">{Math.round(displayStats.totalHours || 0)}</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-3">
                  <p className="text-sm text-gray-600">{t('profile_days_worked') || 'Days Worked'}</p>
                  <p className="text-xl font-bold text-gray-900">{displayStats.daysWorked}</p>
                  <p className="text-xs text-gray-400">{t('profile_days')}</p>
                </div>
              </div>
            </Card>

            {/* Support Chat Button */}
            <Card className="bg-white border-0 shadow-sm p-4">
              <h3 className="font-bold text-gray-900 mb-3">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Have questions or issues? Our support team is here to help you.
              </p>
              <SupportChat />
            </Card>
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="space-y-4">
            <Card className="bg-white border-0 shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Badges</h3>
                <span className="text-sm text-gray-500">
                  {achievements.filter(a => a.earned).length}/{achievements.length} earned
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {achievements.map((achievement) => {
                  const Icon = achievement.icon;
                  return (
                    <div
                      key={achievement.id}
                      className={`text-center p-3 rounded-xl transition-all ${
                        achievement.earned ? 'bg-gray-50' : 'bg-gray-100 opacity-50'
                      }`}
                    >
                      <div className={`w-12 h-12 mx-auto rounded-full bg-gradient-to-br ${achievement.color} flex items-center justify-center mb-2 ${
                        !achievement.earned ? 'grayscale' : ''
                      }`}>
                        <Icon size={24} className="text-white" />
                      </div>
                      <p className="text-xs font-medium text-gray-700">{achievement.name}</p>
                      {!achievement.earned && (
                        <p className="text-[10px] text-gray-400 mt-1">Locked</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Pro Upgrade Card */}
            <Card className="bg-gradient-to-br from-yellow-400 to-orange-500 border-0 p-4 text-white" data-testid="unlock-pro-card">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Crown size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{t('profile_unlock_pro')}</h3>
                  <p className="text-yellow-100 text-sm mt-1">{t('profile_pro_desc')}</p>
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-yellow-100 mb-1">
                      <span>{t('profile_pro_progress')}</span>
                      <span>{Math.min(profile.jobsCompleted || 0, 60)}/60 {t('profile_jobs').toLowerCase()}</span>
                    </div>
                    <Progress value={Math.min(((profile.jobsCompleted || 0) / 60) * 100, 100)} className="h-1.5 bg-white/20" />
                    <p className="text-[10px] text-yellow-100 mt-1">
                      {Math.max(0, 60 - (profile.jobsCompleted || 0))} {t('profile_jobs_remaining')} • {Math.max(0, 30000 - (profile.totalDeliveries || 0)).toLocaleString()} {t('profile_flyers_remaining')}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'settings' && renderSettingsContent()}
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Full Name</Label>
              <Input 
                value={editingProfile.name}
                onChange={(e) => setEditingProfile({...editingProfile, name: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={editingProfile.email}
                onChange={(e) => setEditingProfile({...editingProfile, email: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input 
                value={editingProfile.phone}
                onChange={(e) => setEditingProfile({...editingProfile, phone: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input 
                value={editingProfile.address}
                onChange={(e) => setEditingProfile({...editingProfile, address: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>PayPal Email</Label>
              <Input 
                type="email"
                value={editingProfile.paypalEmail}
                onChange={(e) => setEditingProfile({...editingProfile, paypalEmail: e.target.value})}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Payments will be sent to this email</p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={handleSaveProfile} className="flex-1 bg-orange-500 hover:bg-orange-600">
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => setShowEditProfile(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract Modal */}
      {showContractModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-8 pb-24 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full flex flex-col my-auto" style={{ maxHeight: 'calc(100vh - 140px)' }} data-testid="contract-modal">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900">{t('contract_title')}</h2>
              <button
                onClick={() => setShowContractModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                data-testid="close-contract-modal-btn"
              >
                <X size={18} />
              </button>
            </div>

            {/* Contract Content - Scrollable */}
            <div 
              ref={contractScrollRef}
              onScroll={handleContractScroll}
              onTouchEnd={handleContractTouchEnd}
              className="flex-1 overflow-y-auto px-6 py-4 text-sm text-gray-700 -webkit-overflow-scrolling-touch"
              style={{ WebkitOverflowScrolling: 'touch' }}
              data-testid="contract-content"
            >
              <h3 className="text-xl font-bold text-center mb-4">{contractContent.title}</h3>
              
              <p className="mb-6 whitespace-pre-line">{contractContent.introduction}</p>
              
              {contractContent.sections.map((section, idx) => (
                <div key={idx} className="mb-4">
                  <h4 className="font-bold text-gray-900 mb-2">{section.title}</h4>
                  <p className="whitespace-pre-line">{section.content}</p>
                </div>
              ))}

              {/* Safety Section */}
              <div className="mt-6 mb-4">
                <h4 className="font-bold text-gray-900 mb-2">{contractContent.safetySection.title}</h4>
                <div className="space-y-4">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-1">General Safety</h5>
                    <p className="whitespace-pre-line">{contractContent.safetySection.generalSafety}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-1">Animal Safety</h5>
                    <p className="whitespace-pre-line">{contractContent.safetySection.animalSafety}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-1">Personal Protective Equipment</h5>
                    <p className="whitespace-pre-line">{contractContent.safetySection.ppeRequired}</p>
                  </div>
                </div>
              </div>

              {/* COVID Waiver */}
              <div className="mt-6 mb-4">
                <h4 className="font-bold text-gray-900 mb-2">COVID-19 LIABILITY WAIVER</h4>
                <p className="whitespace-pre-line">{contractContent.covidWaiver}</p>
              </div>

              {/* Scooter Waiver */}
              <div className="mt-6 mb-4">
                <h4 className="font-bold text-gray-900 mb-2">ELECTRIC SCOOTER LIABILITY WAIVER</h4>
                <p className="whitespace-pre-line">{contractContent.scooterWaiver}</p>
              </div>

              {/* Signature Section */}
              <div className="mt-8 pt-4 border-t">
                <p className="font-semibold mb-4">{contractContent.signature}</p>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p><strong>Contractor Name:</strong> {user?.name || '_____________________'}</p>
                  <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                </div>
              </div>

              {!user?.contract_signed && !canSign && (
                <p className="text-center text-orange-600 text-sm mt-4 animate-pulse">
                  ↓ {t('contract_scroll_to_sign')} ↓
                </p>
              )}
              
              {/* Checkbox fallback for mobile users who can't trigger scroll */}
              {!user?.contract_signed && !canSign && (
                <div className="mt-6 pt-4 border-t border-dashed">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acknowledgedRead}
                      onChange={(e) => {
                        setAcknowledgedRead(e.target.checked);
                        if (e.target.checked) setCanSign(true);
                      }}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">
                      I have read and understand the entire Independent Contractor Agreement above
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Modal Footer - Fixed with large padding for iOS bottom nav */}
            <div className="px-6 py-4 border-t bg-gray-50 flex-shrink-0" style={{ paddingBottom: '24px' }}>
              {user?.contract_signed ? (
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <Check size={20} />
                  <span className="font-medium">{t('contract_signed')} - {new Date(user.contract_signed_at).toLocaleDateString()}</span>
                </div>
              ) : (
                <Button
                  onClick={handleSignContract}
                  disabled={signingContract || !canSign}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                  data-testid="sign-contract-btn"
                >
                  {signingContract ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('contract_signing')}
                    </>
                  ) : (
                    <>
                      <ScrollText size={18} className="mr-2" />
                      {t('contract_sign_button')}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Safety Agreement Modal */}
      {showSafetyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-8 pb-24 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full flex flex-col my-auto" style={{ maxHeight: 'calc(100vh - 140px)' }} data-testid="safety-modal">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-red-50">
              <div className="flex items-center gap-3">
                <ShieldAlert size={24} className="text-red-600" />
                <h2 className="text-lg font-bold text-gray-900">Safety & Delivery Rules</h2>
              </div>
              <button
                onClick={() => setShowSafetyModal(false)}
                className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow"
                data-testid="close-safety-modal-btn"
              >
                <X size={18} />
              </button>
            </div>

            {/* Safety Content - Scrollable */}
            <div 
              className="flex-1 overflow-y-auto px-6 py-4"
              style={{ WebkitOverflowScrolling: 'touch' }}
              data-testid="safety-content"
            >
              {/* Warning Banner */}
              <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={24} className="text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-red-800">{safetyWarning.termination}</p>
                    <p className="text-sm text-red-700 mt-2">{safetyWarning.vestRequired}</p>
                  </div>
                </div>
              </div>

              {/* Safety Rules with Checkboxes */}
              <h3 className="text-lg font-bold text-gray-900 mb-4">Delivery Guidelines</h3>
              <p className="text-sm text-gray-600 mb-4">
                Please read each rule carefully and check the box to acknowledge you understand and will follow it.
              </p>
              
              <div className="space-y-4">
                {safetyRules.map((rule, idx) => (
                  <div 
                    key={rule.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      safetyChecks[rule.id] 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={safetyChecks[rule.id] || false}
                        onChange={(e) => setSafetyChecks(prev => ({
                          ...prev,
                          [rule.id]: e.target.checked
                        }))}
                        className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        data-testid={`safety-rule-${rule.id}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{idx + 1}. {rule.title}</span>
                          {rule.severity === 'critical' && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">Critical</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{rule.description}</p>
                      </div>
                    </label>
                  </div>
                ))}
              </div>

              {/* Hazard Protocol */}
              <div className="mt-6 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800">{safetyWarning.hazardProtocol}</p>
                </div>
              </div>

              {/* Progress indicator */}
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Rules acknowledged</span>
                  <span className="font-medium text-gray-900">
                    {Object.values(safetyChecks).filter(Boolean).length} / {safetyRules.length}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${(Object.values(safetyChecks).filter(Boolean).length / safetyRules.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer - Fixed with large padding for iOS bottom nav */}
            <div className="px-6 py-4 border-t bg-gray-50 flex-shrink-0" style={{ paddingBottom: '24px' }}>
              {user?.safety_agreed ? (
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <Check size={20} />
                  <span className="font-medium">Safety Rules Agreed - {new Date(user.safety_agreed_at).toLocaleDateString()}</span>
                </div>
              ) : (
                <Button
                  onClick={handleSignSafety}
                  disabled={signingSafety || !safetyRules.every(rule => safetyChecks[rule.id])}
                  className="w-full bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:bg-gray-400"
                  data-testid="sign-safety-btn"
                >
                  {signingSafety ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Signing...
                    </>
                  ) : (
                    <>
                      <ShieldAlert size={18} className="mr-2" />
                      {safetyRules.every(rule => safetyChecks[rule.id]) 
                        ? 'I Agree to All Safety Rules' 
                        : `Check all ${safetyRules.length - Object.values(safetyChecks).filter(Boolean).length} remaining rules`}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
